"use client";
import { useEffect, useState, useCallback } from "react";

export type ThemeMode = "light" | "dark" | "auto";
export type ResolvedTheme = "light" | "dark";

const THEME_STORAGE_KEY = "theme-preference";

// Get system preference
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

// Get stored theme preference
const getStoredTheme = (): ThemeMode => {
  if (typeof window === "undefined") return "auto";
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "auto") {
      return stored;
    }
  } catch (e) {
    // localStorage not available
  }
  return "auto";
};

// Resolve the actual theme to apply
const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  if (mode === "auto") {
    return getSystemTheme();
  }
  return mode;
};

// Apply theme to document
const applyTheme = (theme: ResolvedTheme) => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(theme);
};

export const useDarkMode = () => {
  const [mounted, setMounted] = useState(false);
  const [themeMode, setThemeModeState] = useState<ThemeMode>("light");
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>("light");

  // Update resolved theme when mode or system preference changes
  const updateResolvedTheme = useCallback((mode: ThemeMode) => {
    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, []);

  // Set theme mode (with persistence and cross-tab sync)
  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      setThemeModeState(mode);

      try {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
      } catch (e) {
        // localStorage not available
      }

      updateResolvedTheme(mode);
    },
    [updateResolvedTheme],
  );

  // Toggle between light and dark (preserving auto if currently auto)
  const toggleTheme = useCallback(() => {
    if (themeMode === "auto") {
      // If auto, switch to opposite of current system preference
      const systemTheme = getSystemTheme();
      setThemeMode(systemTheme === "dark" ? "light" : "dark");
    } else {
      // If explicit mode, toggle to opposite
      setThemeMode(themeMode === "dark" ? "light" : "dark");
    }
  }, [themeMode, setThemeMode]);

  useEffect(() => {
    setMounted(true);
    // Initialize with actual values after mount
    const actualThemeMode = getStoredTheme();
    const actualResolvedTheme = resolveTheme(actualThemeMode);

    setThemeModeState(actualThemeMode);
    setResolvedTheme(actualResolvedTheme);
    // Theme is already applied by ThemeScript, but ensure consistency
    applyTheme(actualResolvedTheme);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Listen for system theme changes (only affects auto mode)
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = () => {
      if (themeMode === "auto") {
        updateResolvedTheme("auto");
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () =>
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
  }, [mounted, themeMode, updateResolvedTheme]);

  useEffect(() => {
    if (!mounted) return;

    // Listen for cross-tab theme changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        const newMode = e.newValue as ThemeMode;
        if (newMode === "light" || newMode === "dark" || newMode === "auto") {
          setThemeModeState(newMode);
          updateResolvedTheme(newMode);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [mounted, updateResolvedTheme]);


  // Return default values during SSR/before mount to prevent hydration issues
  if (!mounted) {
    return {
      themeMode: "light" as ThemeMode,
      resolvedTheme: "light" as ResolvedTheme,
      setThemeMode: () => {},
      toggleTheme: () => {},
      isAuto: false,
      isDark: false,
      isLight: true,
    };
  }
  return {
    themeMode,
    resolvedTheme,
    setThemeMode,
    toggleTheme,
    isAuto: themeMode === "auto",
    isDark: resolvedTheme === "dark",
    isLight: resolvedTheme === "light",
  };
};
